import { useEffect, useMemo, useRef, useState } from "react";
import {
  ERRORS_EXPANSION_ALL_VISIBLE,
  ERRORS_EXPANSION_MANUAL,
  FEEDBACK_VIEW_WARNINGS,
  FILTER_ALL,
  buildErrorsCenterEntries,
  buildErrorsCenterSummary,
  buildVisibleErrors,
  buildVisibleErrorRows,
  createCollapsedExpansionState,
  getResolvedExpandedErrorIds,
  highestErrorTone,
  normalizeFeedbackViewName,
  normalizeFilterKey,
  normalizeText,
  resolveFilterSelectionExpansionState,
  resolveOpenRequestExpansionState,
  resolveFilterLabel,
  toggleExpandedErrorId,
} from "../utils/errorsCenterData.js";

export default function useErrorsCenterState({
  activeView,
  mountNode,
  onActivateView,
  openRequest = null,
  resultPayload,
  viewName = "errors",
}) {
  const rootRef = useRef(null);
  const onActivateViewRef = useRef(onActivateView);
  const handledOpenRequestTokenRef = useRef(0);
  const initialExpansionState = createCollapsedExpansionState();

  const [activeFilter, setActiveFilter] = useState(FILTER_ALL);
  const [activeSource, setActiveSource] = useState("");
  const [expandedErrorIds, setExpandedErrorIds] = useState(initialExpansionState.expandedErrorIds);
  const [expandedErrorGroupIds, setExpandedErrorGroupIds] = useState([]);
  const [expansionMode, setExpansionMode] = useState(initialExpansionState.expansionMode);
  const [openTechnicalErrorId, setOpenTechnicalErrorId] = useState("");
  const [shouldFocusRoot, setShouldFocusRoot] = useState(false);

  useEffect(() => {
    onActivateViewRef.current = onActivateView;
  }, [onActivateView]);

  const normalizedViewName = normalizeFeedbackViewName(viewName);
  const entries = useMemo(
    () => buildErrorsCenterEntries(resultPayload, { viewName: normalizedViewName }),
    [normalizedViewName, resultPayload],
  );
  const visibleEntries = useMemo(() => buildVisibleErrors(entries, activeFilter), [entries, activeFilter]);
  const visibleRows = useMemo(() => buildVisibleErrorRows(visibleEntries), [visibleEntries]);
  const summary = useMemo(
    () => buildErrorsCenterSummary(entries, visibleEntries, activeFilter, activeSource, { viewName: normalizedViewName }),
    [activeFilter, activeSource, entries, normalizedViewName, visibleEntries],
  );
  const resolvedExpandedErrorIds = useMemo(
    () => getResolvedExpandedErrorIds(visibleEntries, { expandedErrorIds, expansionMode }),
    [expandedErrorIds, expansionMode, visibleEntries],
  );
  const resolvedExpandedErrorIdSet = useMemo(() => new Set(resolvedExpandedErrorIds), [resolvedExpandedErrorIds]);
  const resolvedExpandedErrorGroupIds = useMemo(() => {
    const explicitGroupIds = new Set(expandedErrorGroupIds);
    return visibleRows
      .filter((row) => row?.rowType === "group")
      .filter((row) => (
        expansionMode === ERRORS_EXPANSION_ALL_VISIBLE
        || explicitGroupIds.has(row.rowId)
        || row.entries.some((entry) => resolvedExpandedErrorIdSet.has(entry.errorId))
      ))
      .map((row) => row.rowId);
  }, [expandedErrorGroupIds, expansionMode, resolvedExpandedErrorIdSet, visibleRows]);
  const resolvedExpandedErrorGroupIdSet = useMemo(() => new Set(resolvedExpandedErrorGroupIds), [resolvedExpandedErrorGroupIds]);

  const resolvedTechnicalErrorId = resolvedExpandedErrorIdSet.has(openTechnicalErrorId) ? openTechnicalErrorId : "";
  const activeCountLabel = normalizedViewName === FEEDBACK_VIEW_WARNINGS
    ? `${entries.length} ${entries.length > 1 ? "avertissements" : "avertissement"}`
    : `${entries.length} ${entries.length > 1 ? "alertes" : "alerte"}`;
  const activeFilterLabel = activeFilter === FILTER_ALL ? "Tous les filtres" : `Filtre : ${resolveFilterLabel(activeFilter)}`;
  const activeCountTone = highestErrorTone(entries);

  useEffect(() => {
    handledOpenRequestTokenRef.current = 0;
  }, [mountNode]);

  useEffect(() => {
    const requestToken = Number.isInteger(openRequest?.token) ? openRequest.token : 0;
    const requestViewName = normalizeFeedbackViewName(openRequest?.viewName || "errors");
    if (requestToken <= 0 || handledOpenRequestTokenRef.current >= requestToken) {
      return;
    }

    if (requestViewName !== normalizedViewName) {
      return;
    }

    if (!(mountNode instanceof HTMLElement)) {
      return;
    }

    handledOpenRequestTokenRef.current = requestToken;
    const nextFilter = normalizeFilterKey(openRequest?.filter);
    const nextExpansionState = resolveOpenRequestExpansionState(entries, {
      errorId: openRequest?.errorId,
      filter: nextFilter,
      openAll: openRequest?.openAll === true,
    });

    setActiveFilter(nextFilter);
    setActiveSource(normalizeText(openRequest?.source));
    setExpandedErrorIds(nextExpansionState.expandedErrorIds);
    setExpandedErrorGroupIds([]);
    setExpansionMode(nextExpansionState.expansionMode);
    setOpenTechnicalErrorId("");
    setShouldFocusRoot(true);

    if (typeof onActivateViewRef.current === "function") {
      onActivateViewRef.current(normalizedViewName, {
        clearHash: true,
        scroll: openRequest?.scroll !== false,
      });
    }
  }, [entries, mountNode, normalizedViewName, openRequest]);

  useEffect(() => {
    if (!shouldFocusRoot || activeView !== normalizedViewName) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      rootRef.current?.focus({ preventScroll: true });
    });
    setShouldFocusRoot(false);

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [activeView, normalizedViewName, shouldFocusRoot]);

  const handleFilterSelect = (filterKey) => {
    const nextExpansionState = resolveFilterSelectionExpansionState(filterKey);

    setActiveFilter(filterKey);
    setActiveSource("");
    setExpandedErrorIds(nextExpansionState.expandedErrorIds);
    setExpandedErrorGroupIds([]);
    setExpansionMode(nextExpansionState.expansionMode);
    setOpenTechnicalErrorId("");
  };

  const handleReset = () => {
    const nextExpansionState = resolveFilterSelectionExpansionState(FILTER_ALL);

    setActiveFilter(FILTER_ALL);
    setActiveSource("");
    setExpandedErrorIds(nextExpansionState.expandedErrorIds);
    setExpandedErrorGroupIds([]);
    setExpansionMode(nextExpansionState.expansionMode);
    setOpenTechnicalErrorId("");
    setShouldFocusRoot(true);
  };

  const handleSelectEntry = (errorId) => {
    const nextExpansionState = toggleExpandedErrorId(errorId, visibleEntries, {
      expandedErrorIds,
      expansionMode,
    });

    setExpandedErrorIds(nextExpansionState.expandedErrorIds);
    setExpansionMode(nextExpansionState.expansionMode);
    setOpenTechnicalErrorId("");
  };

  const handleSelectErrorGroup = (groupId) => {
    const normalizedGroupId = normalizeText(groupId);
    if (!normalizedGroupId) {
      return;
    }

    const groupRow = visibleRows.find((row) => row?.rowType === "group" && row.rowId === normalizedGroupId);
    const memberErrorIds = new Set(
      (groupRow?.entries || [])
        .map((entry) => normalizeText(entry?.errorId))
        .filter(Boolean),
    );
    const isExpanded = resolvedExpandedErrorGroupIdSet.has(normalizedGroupId);

    setExpandedErrorGroupIds((currentGroupIds) => {
      const nextGroupIds = new Set(currentGroupIds);
      if (isExpanded) {
        nextGroupIds.delete(normalizedGroupId);
      } else {
        nextGroupIds.add(normalizedGroupId);
      }
      return Array.from(nextGroupIds);
    });

    if (isExpanded && memberErrorIds.size > 0) {
      setExpandedErrorIds((currentErrorIds) => currentErrorIds.filter((errorId) => !memberErrorIds.has(normalizeText(errorId))));
      setOpenTechnicalErrorId((currentValue) => (memberErrorIds.has(normalizeText(currentValue)) ? "" : currentValue));
    }

    setExpansionMode(ERRORS_EXPANSION_MANUAL);
  };

  const handleToggleTechnical = (errorId) => {
    if (!resolvedExpandedErrorIdSet.has(errorId) && errorId) {
      setExpandedErrorIds([errorId]);
      setExpansionMode(ERRORS_EXPANSION_MANUAL);
    }
    setOpenTechnicalErrorId((currentValue) => (currentValue === errorId ? "" : errorId));
  };

  return {
    activeCountLabel,
    activeCountTone,
    activeFilter,
    activeFilterLabel,
    entries,
    handleFilterSelect,
    handleReset,
    handleSelectErrorGroup,
    handleSelectEntry,
    handleToggleTechnical,
    resolvedExpandedErrorGroupIds,
    resolvedExpandedErrorIds,
    resolvedTechnicalErrorId,
    rootRef,
    summary,
    visibleEntries,
    visibleRows,
  };
}
